import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import PasswordInput from "@/components/PasswordInput";
import FieldError from "@/components/FieldError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { validateForm, required, email as emailRule } from "@/lib/validators";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  const runValidation = (values) => validateForm(values, {
    email: [(v) => required(v, "Email"), (v) => emailRule(v)],
    password: [(v) => required(v, "Password")],
  });

  const submit = async (e) => {
    e.preventDefault();
    const values = { email, password };
    const { errors: e2, valid } = runValidation(values);
    setErrors(e2);
    setTouched({ email: true, password: true });
    if (!valid) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", values);
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
      const to = location.state?.from?.pathname || (data.role === "admin" ? "/admin" : "/dashboard");
      navigate(to, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    const { errors: e2 } = runValidation({ email, password });
    setErrors(e2);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell testId="login-page" title="Welcome back" subtitle="Log in to your TikBoost workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur("email")}
            aria-invalid={touched.email && !!errors.email ? "true" : undefined}
            aria-describedby={errors.email ? "login-email-error" : undefined}
            data-testid="login-email"
          />
          {touched.email && <FieldError id="login-email-error" message={errors.email} testId="login-email-error" />}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-cyan-400 hover:underline" data-testid="login-forgot">Forgot?</Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => handleBlur("password")}
            invalid={touched.password && !!errors.password}
            aria-describedby={errors.password ? "login-password-error" : undefined}
            testId="login-password"
          />
          {touched.password && <FieldError id="login-password-error" message={errors.password} testId="login-password-error" />}
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 border-0 hover:brightness-110" data-testid="login-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground uppercase tracking-widest">
        <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
      </div>

      <Button onClick={google} variant="outline" className="w-full rounded-full" data-testid="login-google">
        <GoogleIcon /> Continue with Google
      </Button>

      <p className="mt-8 text-sm text-muted-foreground text-center">
        Don't have an account?{" "}
        <Link to="/register" className="text-cyan-400 hover:underline" data-testid="link-register">Sign up</Link>
      </p>
    </AuthShell>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.8h5.3c-.2 1.4-1.6 4-5.3 4-3.2 0-5.8-2.6-5.8-5.9S8.8 6.2 12 6.2c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.7 3.8 14.6 3 12 3 6.9 3 2.8 7.1 2.8 12.2S6.9 21.4 12 21.4c6.9 0 9.4-4.8 9.4-8.7 0-.6-.1-1.1-.2-1.5H12z" />
    </svg>
  );
}
